import { Box, Stack, Typography, useTheme } from '@mui/material';
import type { ReactNode } from 'react';

/**
 * Single number with a label. Used in dashboards + at the top of list pages
 * to surface "how many DLQ rows are there?" without making the user scroll.
 *
 * Tone choices map to the same vocabulary as StatusPill so the visual
 * language stays coherent.
 */
export function MetricStat({
  label,
  value,
  hint,
  tone = 'neutral',
}: {
  label: ReactNode;
  value: ReactNode;
  hint?: ReactNode;
  tone?: 'neutral' | 'success' | 'warning' | 'danger' | 'info';
}) {
  const theme = useTheme();
  const accent = (() => {
    if (tone === 'neutral') return theme.palette.text.primary;
    if (tone === 'success') return theme.palette.status.success;
    if (tone === 'warning') return theme.palette.status.warning;
    if (tone === 'danger')  return theme.palette.status.danger;
    return theme.palette.status.info;
  })();

  return (
    <Box
      sx={{
        bgcolor: theme.palette.surface.card,
        border: `1px solid ${theme.palette.border.subtle}`,
        borderRadius: theme.radius.md + 'px',
        p: 2.5,
        minWidth: 0,
      }}
    >
      <Stack spacing={0.75}>
        <Typography variant="overline" color="text.tertiary">
          {label}
        </Typography>
        <Typography
          sx={{
            fontSize: 28,
            fontWeight: 600,
            lineHeight: 1.1,
            letterSpacing: -0.4,
            color: accent,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {value}
        </Typography>
        {hint && (
          <Typography variant="caption" color="text.secondary">
            {hint}
          </Typography>
        )}
      </Stack>
    </Box>
  );
}
